"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import styles from "./api.module.css";

import { API_STRUCTURE } from "./apiDocsData";


const LANGUAGE_ORDER = ["curl", "node", "python", "ruby", "go", "php", "csharp"];
const LANGUAGE_LABELS = {
  curl: "cURL",
  node: "Node.js",
  python: "Python",
  ruby: "Ruby",
  go: "Go",
  php: "PHP",
  csharp: "C#",
};

// Simple syntax highlighter for code blocks
function highlightCode(code, language) {
  if (!code) return null;

  const tokens = [];
  let remaining = code;
  let key = 0;

  // Define patterns for different token types (order matters!)
  const patterns = [
    // Comments
    { regex: /^(#[^\n]*|\/\/[^\n]*|\/\*[\s\S]*?\*\/)/m, type: 'comment' },
    // Object keys in JSON (must come before strings)
    { regex: /^"([^"]+)"(?=\s*:)/, type: 'key' },
    // Strings (double quotes)
    { regex: /^"(?:[^"\\]|\\.)*"/, type: 'string' },
    // Strings (single quotes)
    { regex: /^'(?:[^'\\]|\\.)*'/, type: 'string' },
    // Strings (backticks/template literals)
    { regex: /^`(?:[^`\\]|\\.)*`/, type: 'string' },
    // URLs
    { regex: /^https?:\/\/[^\s"'`\]\\]+/, type: 'string' },
    // Boolean and null values
    { regex: /^\b(true|false|null|nil|None|True|False)\b/, type: 'keyword' },
    // Numbers
    { regex: /^\b\d+\.?\d*\b/, type: 'number' },
    // Keywords (common across languages)
    { regex: /^\b(const|let|var|function|async|await|return|if|else|for|while|import|from|export|default|new|class|extends|public|private|static|void|int|string|bool|def|end|require|module|package|func|using|namespace|Task|echo|puts|print|fmt)\b/, type: 'keyword' },
    // HTTP methods
    { regex: /^\b(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\b/, type: 'method' },
    // Curl command
    { regex: /^curl\b/, type: 'function' },
    // Function calls
    { regex: /^\b([a-zA-Z_][a-zA-Z0-9_]*)\s*(?=\()/, type: 'function' },
    // Properties/methods after dot
    { regex: /^\.([a-zA-Z_][a-zA-Z0-9_]*)/, type: 'property' },
    // Operators
    { regex: /^(=>|->|::|\.\.\.|\?\?|&&|\|\||[+\-*/%=<>!&|^~]+)/, type: 'operator' },
    // Punctuation
    { regex: /^[{}[\]();,:]/, type: 'punctuation' },
    // Variable names (after $ for PHP/shell)
    { regex: /^\$[a-zA-Z_][a-zA-Z0-9_]*/, type: 'variable' },
    // Curl flags
    { regex: /^-[a-zA-Z]+\b/, type: 'flag' },
    // Backslash line continuation
    { regex: /^\\$/, type: 'punctuation' },
  ];

  while (remaining.length > 0) {
    let matched = false;

    for (const { regex, type } of patterns) {
      const match = remaining.match(regex);
      if (match) {
        const text = match[0];
        if (type === 'property') {
          // Include the dot in the token
          tokens.push(<span key={key++} className={styles.tokenPunctuation}>.</span>);
          tokens.push(<span key={key++} className={styles[`token${type.charAt(0).toUpperCase() + type.slice(1)}`]}>{text.slice(1)}</span>);
        } else {
          const className = styles[`token${type.charAt(0).toUpperCase() + type.slice(1)}`];
          tokens.push(<span key={key++} className={className}>{text}</span>);
        }
        remaining = remaining.slice(text.length);
        matched = true;
        break;
      }
    }

    if (!matched) {
      // No pattern matched, consume one character as plain text
      const char = remaining[0];
      // Merge with previous text token if possible
      const lastToken = tokens[tokens.length - 1];
      if (lastToken && typeof lastToken === 'string') {
        tokens[tokens.length - 1] = lastToken + char;
      } else if (tokens.length > 0 && lastToken?.props?.className === undefined) {
        tokens.push(char);
      } else {
        tokens.push(char);
      }
      remaining = remaining.slice(1);
    }
  }

  return tokens;
}

// Simple markdown renderer for descriptions
function renderDescription(text) {
  if (!text) return null;

  const lines = text.split('\n');
  const elements = [];
  let currentList = [];
  let key = 0;

  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(<ul key={`list-${key++}`} className={styles.descriptionList}>{currentList}</ul>);
      currentList = [];
    }
  };

  const renderInline = (str) => {
    // Replace **text** with <strong>
    const parts = [];
    let remaining = str;
    let partKey = 0;

    while (remaining.length > 0) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      const codeMatch = remaining.match(/`([^`]+)`/);

      // Find which match comes first
      let firstMatch = null;
      let matchType = null;

      if (boldMatch && (!codeMatch || boldMatch.index <= codeMatch.index)) {
        firstMatch = boldMatch;
        matchType = 'bold';
      } else if (codeMatch) {
        firstMatch = codeMatch;
        matchType = 'code';
      }

      if (firstMatch) {
        // Add text before match
        if (firstMatch.index > 0) {
          parts.push(remaining.slice(0, firstMatch.index));
        }
        // Add the formatted element
        if (matchType === 'bold') {
          parts.push(<strong key={partKey++}>{firstMatch[1]}</strong>);
        } else {
          parts.push(<code key={partKey++} className={styles.inlineCode}>{firstMatch[1]}</code>);
        }
        remaining = remaining.slice(firstMatch.index + firstMatch[0].length);
      } else {
        parts.push(remaining);
        break;
      }
    }

    return parts;
  };

  lines.forEach((line) => {
    const trimmed = line.trim();

    if (trimmed.startsWith('- ')) {
      // List item
      currentList.push(<li key={`li-${key++}`}>{renderInline(trimmed.slice(2))}</li>);
    } else if (trimmed.startsWith('**') && trimmed.endsWith('**') && !trimmed.slice(2, -2).includes('**')) {
      // Bold header line
      flushList();
      elements.push(<div key={key++} className={styles.descriptionHeader}>{trimmed.slice(2, -2)}</div>);
    } else if (trimmed === '') {
      flushList();
    } else {
      flushList();
      elements.push(<p key={key++} className={styles.descriptionText}>{renderInline(trimmed)}</p>);
    }
  });

  flushList();
  return <div className={styles.descriptionContent}>{elements}</div>;
}

export default function ApiDocsPage() {
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [expandedSections, setExpandedSections] = useState(API_STRUCTURE.map((s) => s.id));
  const [searchQuery, setSearchQuery] = useState("");
  const [activeExampleTabs, setActiveExampleTabs] = useState({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const articleId = params.get("article");
    if (articleId) {
      for (const section of API_STRUCTURE) {
        const found = section.articles.find((article) => article.id === articleId);
        if (found) {
          setSelectedArticle({ ...found, sectionId: section.id, sectionTitle: section.title });
          break;
        }
      }
    }
  }, []);

  const filteredStructure = useMemo(() => {
    if (!searchQuery.trim()) return API_STRUCTURE;
    const query = searchQuery.toLowerCase();
    return API_STRUCTURE.map((section) => ({
      ...section,
      articles: section.articles.filter(
        (article) =>
          article.title.toLowerCase().includes(query) ||
          article.description?.toLowerCase().includes(query) ||
          article.path?.toLowerCase().includes(query)
      ),
    })).filter((section) => section.articles.length > 0);
  }, [searchQuery]);

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleBackToHome = () => {
    setSelectedArticle(null);
    window.history.pushState({}, "", "/help/api");
  };

  const getMethodClass = (method) => {
    if (method === "POST") return `${styles.method} ${styles.methodPost}`;
    if (method === "PATCH") return `${styles.method} ${styles.methodPatch}`;
    if (method === "DELETE") return `${styles.method} ${styles.methodDelete}`;
    return styles.method;
  };

  const renderEndpoint = (article) => {
    const examples = article.examples || {};
    const languages = LANGUAGE_ORDER.filter((lang) => examples[lang])
      .concat(Object.keys(examples).filter((lang) => !LANGUAGE_ORDER.includes(lang)));
    const currentLang = activeExampleTabs[article.id] || languages[0];

    return (
      <div className={styles.endpointCard}>
        <div className={styles.endpointHeader}>
          <span className={getMethodClass(article.method)}>{article.method}</span>
          <span className={styles.endpointPath}>{article.path}</span>
        </div>
        <div className={styles.endpointMeta}>
          {article.scopes?.map((scope) => (
            <span key={scope} className={styles.metaPill}>{scope}</span>
          ))}
        </div>
        {renderDescription(article.description)}
        <div>
          <div className={styles.sectionList}>
            <div className={styles.exampleTabs}>
              {languages.map((lang) => (
                <button
                  key={lang}
                  className={`${styles.exampleTab} ${currentLang === lang ? styles.exampleTabActive : ""}`}
                  onClick={() => setActiveExampleTabs((prev) => ({ ...prev, [article.id]: lang }))}
                >
                  {LANGUAGE_LABELS[lang] || lang}
                </button>
              ))}
            </div>
            <pre className={styles.codeBlock}>
              <code>{highlightCode(examples[currentLang], currentLang)}</code>
            </pre>
          </div>
        </div>
        <div>
          <div className={styles.metaPill}>Expected response</div>
          <pre className={styles.codeBlock}>
            <code>{highlightCode(article.response, 'json')}</code>
          </pre>
        </div>
      </div>
    );
  };

  const renderContent = (content) => {
    const lines = content.trim().split("\n");
    const elements = [];
    let currentList = [];

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className={styles.list}>
            {currentList}
          </ul>
        );
        currentList = [];
      }
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      if (trimmed.startsWith("# ")) {
        flushList();
        elements.push(<h2 key={index} className={styles.articleTitle}>{trimmed.slice(2)}</h2>);
      } else if (trimmed.startsWith("## ")) {
        flushList();
        elements.push(<h3 key={index} className={styles.sectionTitle}>{trimmed.slice(3)}</h3>);
      } else if (trimmed.startsWith("- ")) {
        currentList.push(<li key={index}>{trimmed.slice(2)}</li>);
      } else if (trimmed.startsWith("\t")) {
        flushList();
        elements.push(
          <pre key={index} className={styles.codeBlock}>
            <code>{highlightCode(trimmed.replace(/^\t/, ""), 'text')}</code>
          </pre>
        );
      } else if (trimmed === "") {
        flushList();
      } else {
        flushList();
        elements.push(<p key={index} className={styles.articleSubtitle}>{trimmed}</p>);
      }
    });

    flushList();
    return elements;
  };

  return (
    <div className={styles.layout}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.headerLeft}>
            <Link href="/" className={styles.logoLink}>
              <Image src="/logoFlare.png" alt="Flare" width={80} height={18} priority quality={100} />
            </Link>
            <span className={styles.headerDivider}>/</span>
            <span className={styles.headerTitle}>API Docs</span>
          </div>
          <div className={styles.headerRight}>
            <Link href="/help" className={styles.headerLinkSubtle}>App Docs</Link>
            <Link href="/login" className={styles.headerLinkSubtle}>Sign in</Link>
            <Link href="/register" className={styles.headerButtonPrimary}>Get Started</Link>
          </div>
        </div>
      </header>

      <div className={styles.main}>
        <aside className={styles.sidebar}>
          <div className={styles.sidebarSearch}>
            <svg className={styles.searchIcon} width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="6" cy="6" r="4.5" />
              <path d="M10 10l3 3" />
            </svg>
            <input
              type="text"
              placeholder="Search API docs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <nav className={styles.nav}>
            {filteredStructure.map((section) => (
              <div key={section.id} className={styles.navSection}>
                <button
                  className={styles.navSectionHeader}
                  onClick={() => toggleSection(section.id)}
                >
                  <span>{section.title}</span>
                  <svg
                    className={`${styles.chevron} ${expandedSections.includes(section.id) ? styles.chevronOpen : ""}`}
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
                {expandedSections.includes(section.id) && (
                  <ul className={styles.navList}>
                    {section.articles.map((article) => (
                      <li key={article.id}>
                        <Link
                          href={`/help/api/${article.id}`}
                          className={`${styles.navItem} ${selectedArticle?.id === article.id ? styles.navItemActive : ""}`}
                        >
                          {article.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </nav>
        </aside>

        <main className={styles.content}>
          {selectedArticle ? (
            <article className={styles.article}>
              <div className={styles.breadcrumb}>
                <button onClick={handleBackToHome} className={styles.breadcrumbLink}>API Docs</button>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m9 18 6-6-6-6" />
                </svg>
                <span>{selectedArticle.sectionTitle}</span>
              </div>
              <h1 className={styles.articleTitle}>{selectedArticle.title}</h1>
              {selectedArticle.method ? (
                renderEndpoint(selectedArticle)
              ) : (
                <div className={styles.sectionList}>{renderContent(selectedArticle.content)}</div>
              )}
            </article>
          ) : (
            <div className={styles.home}>
              <h1 className={styles.homeTitle}>Flare API Documentation</h1>
              <p className={styles.homeSubtitle}>
                Explore endpoints for uploads, embeds, and automation. Use the sidebar to jump into specific flows.
              </p>
              <div className={styles.exportGrid}>
                <a className={styles.exportCard} href="/api/docs/openapi" target="_blank" rel="noreferrer">
                  <div className={styles.exportTitle}>OpenAPI</div>
                  <div className={styles.exportText}>Machine-readable OpenAPI 3.1 spec.</div>
                </a>
                <a className={styles.exportCard} href="/api/docs/schema" target="_blank" rel="noreferrer">
                  <div className={styles.exportTitle}>JSON Schema</div>
                  <div className={styles.exportText}>Request/response JSON schema export.</div>
                </a>
                <a className={styles.exportCard} href="/api/docs/llm" target="_blank" rel="noreferrer">
                  <div className={styles.exportTitle}>LLM Doc</div>
                  <div className={styles.exportText}>Plain-text docs optimized for LLMs.</div>
                </a>
              </div>
              <div className={styles.homeGrid}>
                {API_STRUCTURE.map((section) => (
                  <div key={section.id} className={styles.homeCard}>
                    <div className={styles.homeCardTitle}>{section.title}</div>
                    <ul className={styles.homeCardList}>
                      {section.articles.slice(0, 3).map((article) => (
                        <li key={article.id}>
                          <Link
                            href={`/help/api/${article.id}`}
                            className={styles.homeCardLink}
                          >
                            {article.title}
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="m9 18 6-6-6-6" />
                            </svg>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
